import { getDocs, query, where } from 'firebase/firestore';
import { getPatientPrograms } from './patientProgramService';
import { tenantCollection } from './tenantContext';
import { formatLastFirstName, formatReportDateYmd, resolveClientShortName } from '../utils/reportPatientJoins';
import {
  ymdLocal,
  isRpmProgram,
  toDate,
  formatShortMdY,
  isSessionOnHoldAt,
  groupHoldAuditsByProgram,
  findOpenSessionAt,
  HOLD_ENDING_ACTIONS,
} from './rpmCensusUtils';

const AUDIT_COL = 'patientProgramAuditLog';
/**
 * Audit actions the report consumes. hold_set plus the hold-ending actions
 * drive point-in-time on-hold checks; only hold_set, hold_lifted and
 * program_reopened produce activity rows.
 */
const REPORT_AUDIT_ACTIONS = new Set(['hold_set', ...HOLD_ENDING_ACTIONS]);
const UNASSIGNED = '__unassigned__';
const FETCH_CONCURRENCY = 15;
const PATIENT_IN_CHUNK = 30;

const CHANGE_TYPES = {
  enrolled: 'Enrolled',
  returned: 'Returned from hold',
  onHold: 'On hold',
  discharged: 'Discharged',
  closedOutreach: 'Closed \u2014 Outreach',
  closedPreService: 'Closed \u2014 Pre-Service',
  reopened: 'Reopened',
};

/** Change types that put a patient into the active census. */
const ENTRY_CHANGE_KEYS = new Set(['enrolled', 'returned', 'reopened']);
/** Change types that take a patient out of the active census. */
const EXIT_CHANGE_KEYS = new Set(['discharged', 'onHold']);
const NO_VALUE = '\u2014';

async function mapWithConcurrency(items, concurrency, fn) {
  const results = [];
  for (let i = 0; i < items.length; i += concurrency) {
    const chunk = items.slice(i, i + concurrency);
    // eslint-disable-next-line no-await-in-loop
    const chunkResults = await Promise.all(chunk.map(fn));
    results.push(...chunkResults);
  }
  return results;
}

function resolveClientId(patient, clientSites) {
  if (!patient) return UNASSIGNED;
  const siteMap = new Map((clientSites || []).map((s) => [s.id, String(s.clientId || '')]));
  const clientId = siteMap.get(patient.clientSiteId) || patient.clientId || '';
  return clientId || UNASSIGNED;
}

function resolveClientLabel(clientId, clients, shortName) {
  if (clientId === UNASSIGNED) return 'Unassigned (no client)';
  if (shortName) return shortName;
  const client = (clients || []).find((c) => c.id === clientId);
  return client?.clientShortName || client?.clientName || clientId;
}

/** Compact client label for patient-level tables, where space is tight. */
function resolveClientShortLabel(clientId, clients, shortName) {
  if (clientId === UNASSIGNED) return 'Unassigned';
  if (shortName) return shortName;
  const client = (clients || []).find((c) => c.id === clientId);
  return client?.clientShortName || client?.clientName || clientId;
}

function emptyClientBucket(clientId, clientName) {
  return {
    clientId,
    clientName,
    openingIds: new Set(),
    endingIds: new Set(),
    opening: 0,
    added: 0,
    removed: 0,
    ending: 0,
    net: 0,
    pctChange: null,
    pctChangeLabel: '—',
  };
}

/** Count of elements in `a` that are not in `b`. */
function setDiffSize(a, b) {
  let n = 0;
  a.forEach((id) => {
    if (!b.has(id)) n += 1;
  });
  return n;
}

/**
 * Index activity rows into each patient's latest census entry/exit move plus a
 * count of in-period moves, so rosters can show what last put a patient in or
 * out of the census even when they churned several times.
 * @returns {Map<string, { latestEntry: object|null, latestExit: object|null, moves: number }>}
 */
function indexCensusMoves(activityRows) {
  const byPatient = new Map();
  activityRows.forEach((row) => {
    const isEntry = ENTRY_CHANGE_KEYS.has(row.changeTypeKey);
    const isExit = EXIT_CHANGE_KEYS.has(row.changeTypeKey);
    if (!isEntry && !isExit) return;
    if (!byPatient.has(row.patientId)) {
      byPatient.set(row.patientId, { latestEntry: null, latestExit: null, moves: 0 });
    }
    const entry = byPatient.get(row.patientId);
    entry.moves += 1;
    if (isEntry && (!entry.latestEntry || row.sortMs >= entry.latestEntry.sortMs)) entry.latestEntry = row;
    if (isExit && (!entry.latestExit || row.sortMs >= entry.latestExit.sortMs)) entry.latestExit = row;
  });
  return byPatient;
}

function sortByPatientName(rows) {
  return rows.sort((a, b) =>
    (a.patientName || '').localeCompare(b.patientName || '', undefined, { sensitivity: 'base' }),
  );
}

function pctChange(opening, net) {
  if (!opening) return { pctChange: null, pctChangeLabel: '—' };
  const pct = (net / opening) * 100;
  const rounded = Math.round(pct * 10) / 10;
  const label = `${rounded > 0 ? '+' : ''}${rounded}%`;
  return { pctChange: rounded, pctChangeLabel: label };
}

/**
 * Fetch RPM programs + hold audit rows for scoped patients.
 * @returns {{ programsByPatientId: Map<string, object[]>, holdAudits: object[] }}
 */
export async function fetchVolumeActivityInputs(tenantId, patientIds) {
  const programsByPatientId = new Map();
  const holdAudits = [];
  if (!tenantId || !patientIds?.length) {
    return { programsByPatientId, holdAudits };
  }

  const programLists = await mapWithConcurrency(patientIds, FETCH_CONCURRENCY, async (pid) => {
    try {
      const programs = await getPatientPrograms(pid);
      return { pid, programs: (programs || []).filter(isRpmProgram) };
    } catch {
      return { pid, programs: [] };
    }
  });
  programLists.forEach(({ pid, programs }) => {
    programsByPatientId.set(pid, programs);
  });

  const col = tenantCollection(tenantId, AUDIT_COL);
  for (let i = 0; i < patientIds.length; i += PATIENT_IN_CHUNK) {
    const chunk = patientIds.slice(i, i + PATIENT_IN_CHUNK);
    // eslint-disable-next-line no-await-in-loop
    const snap = await getDocs(query(col, where('patientId', 'in', chunk)));
    snap.docs.forEach((d) => {
      const data = d.data() || {};
      if (!REPORT_AUDIT_ACTIONS.has(data.action)) return;
      const abbr = String(data.serviceTypeAbbreviation || '').toUpperCase();
      if (abbr && abbr !== 'RPM') return;
      holdAudits.push({ id: d.id, ...data });
    });
  }

  return { programsByPatientId, holdAudits };
}

/**
 * Build Volume Activity KPIs, by-client rollup, and activity detail rows.
 */
export function buildVolumeActivityReport({
  patients,
  programsByPatientId,
  holdAudits,
  clients,
  clientSites,
  period,
}) {
  const start = period?.start ?? null;
  const end = period?.end ?? null;
  const startYmd = ymdLocal(start);
  const endYmd = ymdLocal(end);

  const emptyKpis = {
    opening: 0,
    added: 0,
    removed: 0,
    ending: 0,
    net: 0,
    pctChange: null,
    pctChangeLabel: '—',
  };

  if (!startYmd || !endYmd || !Array.isArray(patients)) {
    return {
      kpis: emptyKpis,
      byClient: [],
      activityRows: [],
      openingRoster: [],
      addedRoster: [],
      removedRoster: [],
    };
  }

  const auditsByProgram = groupHoldAuditsByProgram(holdAudits);

  const openingPatientIds = new Set();
  const endingPatientIds = new Set();
  const byClientMap = new Map();
  const activityRows = [];
  const patientMetaById = new Map();

  const ensureClient = (clientId, clientName) => {
    if (!byClientMap.has(clientId)) {
      byClientMap.set(clientId, emptyClientBucket(clientId, clientName));
    }
    return byClientMap.get(clientId);
  };

  patients.forEach((patient) => {
    const patientId = patient.id;
    if (!patientId) return;
    const programs = programsByPatientId?.get?.(patientId) || [];
    if (!programs.length) return;

    const clientId = resolveClientId(patient, clientSites);
    const clientShortName = resolveClientShortName(patient, clients, clientSites);
    const clientName = resolveClientLabel(clientId, clients, clientShortName);
    const clientShortLabel = resolveClientShortLabel(clientId, clients, clientShortName);
    const bucket = ensureClient(clientId, clientName);
    const patientName = formatLastFirstName(patient) || patientId;
    const mrn = patient.mrn || '';

    let patientOpen = false;
    let patientEnd = false;
    let openingEnrollYmd = '';

    programs.forEach((program) => {
      const sessions = Array.isArray(program.sessions) ? program.sessions : [];
      const auditKey = `${patientId}|${program.id || ''}`;
      const programAudits = auditsByProgram.get(auditKey) || [];

      const openSessionAtStart = findOpenSessionAt(sessions, startYmd, 'start');
      if (openSessionAtStart && !isSessionOnHoldAt(programAudits, sessions, openSessionAtStart, start, 'lt')) {
        patientOpen = true;
        if (!openingEnrollYmd) {
          openingEnrollYmd = formatReportDateYmd(openSessionAtStart.enrollmentDate) || '';
        }
      }

      const openSessionAtEnd = findOpenSessionAt(sessions, endYmd, 'end');
      if (openSessionAtEnd && !isSessionOnHoldAt(programAudits, sessions, openSessionAtEnd, end, 'lte')) {
        patientEnd = true;
      }

      sessions.forEach((session) => {
        const enroll = formatReportDateYmd(session?.enrollmentDate);
        if (enroll && enroll >= startYmd && enroll <= endYmd) {
          activityRows.push({
            date: formatShortMdY(enroll),
            changeType: CHANGE_TYPES.enrolled,
            changeTypeKey: 'enrolled',
            patientId,
            patientName,
            mrn,
            clientId,
            clientName,
            clientShortName: clientShortLabel,
            reason: '',
            sortMs: Date.parse(`${enroll}T12:00:00`) || 0,
          });
        }
        const discharge = formatReportDateYmd(session?.dischargeDate);
        if (discharge && discharge >= startYmd && discharge <= endYmd) {
          activityRows.push({
            date: formatShortMdY(discharge),
            changeType: CHANGE_TYPES.discharged,
            changeTypeKey: 'discharged',
            patientId,
            patientName,
            mrn,
            clientId,
            clientName,
            clientShortName: clientShortLabel,
            reason: session?.dischargeReasonName || '',
            sortMs: Date.parse(`${discharge}T12:00:00`) || 0,
          });
        }
        // Closed before enrollment (from Outreach or Pre-Service) — never an
        // enrolled patient, so KPIs are untouched; surfaced as activity only.
        const closed = formatReportDateYmd(session?.closedDate);
        if (closed && closed >= startYmd && closed <= endYmd) {
          const fromPreService = session?.closedFromStatus === 'preService';
          activityRows.push({
            date: formatShortMdY(closed),
            changeType: fromPreService ? CHANGE_TYPES.closedPreService : CHANGE_TYPES.closedOutreach,
            changeTypeKey: fromPreService ? 'closedPreService' : 'closedOutreach',
            patientId,
            patientName,
            mrn,
            clientId,
            clientName,
            clientShortName: clientShortLabel,
            reason: session?.closedReasonName || '',
            sortMs: Date.parse(`${closed}T12:00:00`) || 0,
          });
        }
      });

      programAudits.forEach((a) => {
        const at = toDate(a.createdAt);
        if (!at || at < start || at > end) return;
        const dateYmd = ymdLocal(at);
        if (a.action === 'hold_set') {
          activityRows.push({
            date: formatShortMdY(dateYmd),
            changeType: CHANGE_TYPES.onHold,
            changeTypeKey: 'onHold',
            patientId,
            patientName,
            mrn,
            clientId,
            clientName,
            clientShortName: clientShortLabel,
            reason: a.changes?.holdReasonName?.new || '',
            sortMs: at.getTime(),
          });
        } else if (a.action === 'hold_lifted') {
          activityRows.push({
            date: formatShortMdY(dateYmd),
            changeType: CHANGE_TYPES.returned,
            changeTypeKey: 'returned',
            patientId,
            patientName,
            mrn,
            clientId,
            clientName,
            clientShortName: clientShortLabel,
            reason: '',
            sortMs: at.getTime(),
          });
        } else if (a.action === 'program_reopened') {
          activityRows.push({
            date: formatShortMdY(dateYmd),
            changeType: CHANGE_TYPES.reopened,
            changeTypeKey: 'reopened',
            patientId,
            patientName,
            mrn,
            clientId,
            clientName,
            clientShortName: clientShortLabel,
            reason: '',
            sortMs: at.getTime(),
          });
        }
      });
    });

    if (patientOpen) {
      openingPatientIds.add(patientId);
      bucket.openingIds.add(patientId);
    }
    if (patientEnd) {
      endingPatientIds.add(patientId);
      bucket.endingIds.add(patientId);
    }
    if (patientOpen || patientEnd) {
      patientMetaById.set(patientId, {
        patientId,
        patientName,
        mrn,
        clientId,
        clientName,
        clientShortName: clientShortLabel,
        openingEnrollYmd,
      });
    }
  });

  // Ensure clients that only appear in activity detail still show when they have
  // opening/ending/net patient moves (sets already populated above).
  activityRows.forEach((row) => {
    ensureClient(row.clientId, row.clientName);
  });

  byClientMap.forEach((bucket) => {
    bucket.opening = bucket.openingIds.size;
    bucket.ending = bucket.endingIds.size;
    bucket.added = setDiffSize(bucket.endingIds, bucket.openingIds);
    bucket.removed = setDiffSize(bucket.openingIds, bucket.endingIds);
    bucket.net = bucket.ending - bucket.opening;
    const pct = pctChange(bucket.opening, bucket.net);
    bucket.pctChange = pct.pctChange;
    bucket.pctChangeLabel = pct.pctChangeLabel;
    delete bucket.openingIds;
    delete bucket.endingIds;
  });

  const byClient = [...byClientMap.values()]
    .filter((b) => b.opening || b.ending || b.added || b.removed || activityRows.some((r) => r.clientId === b.clientId))
    .sort((a, b) => {
      if (a.clientId === UNASSIGNED) return 1;
      if (b.clientId === UNASSIGNED) return -1;
      return a.clientName.localeCompare(b.clientName, undefined, { sensitivity: 'base' });
    });

  activityRows.sort((a, b) => {
    if (a.sortMs !== b.sortMs) return a.sortMs - b.sortMs;
    return (a.patientName || '').localeCompare(b.patientName || '', undefined, { sensitivity: 'base' });
  });

  const opening = openingPatientIds.size;
  const ending = endingPatientIds.size;
  const added = setDiffSize(endingPatientIds, openingPatientIds);
  const removed = setDiffSize(openingPatientIds, endingPatientIds);
  const net = ending - opening;
  const pct = pctChange(opening, net);

  // Rosters mirror the KPI sets, so a patient who churns mid-period but ends
  // where they started appears in neither Added nor Removed.
  const movesByPatient = indexCensusMoves(activityRows);
  const metaFor = (patientId) =>
    patientMetaById.get(patientId) || { patientId, patientName: patientId, mrn: '', clientShortName: '' };

  const openingRoster = sortByPatientName(
    [...openingPatientIds].map((patientId) => {
      const meta = metaFor(patientId);
      const enrolled = meta.openingEnrollYmd || '';
      return {
        patientId,
        patientName: meta.patientName,
        mrn: meta.mrn,
        clientShortName: meta.clientShortName,
        enrolledDate: enrolled ? formatShortMdY(enrolled) : NO_VALUE,
        sortMs: enrolled ? Date.parse(`${enrolled}T12:00:00`) || 0 : 0,
        endingStatus: endingPatientIds.has(patientId) ? 'Active' : 'Removed',
      };
    }),
  );

  const rosterFromMove = (patientId, move) => {
    const meta = metaFor(patientId);
    return {
      patientId,
      patientName: meta.patientName,
      mrn: meta.mrn,
      clientShortName: meta.clientShortName,
      date: move ? move.date : NO_VALUE,
      sortMs: move ? move.sortMs : 0,
      changeType: move ? move.changeType : NO_VALUE,
      reason: move?.reason || '',
      moves: movesByPatient.get(patientId)?.moves || 0,
    };
  };

  const addedRoster = sortByPatientName(
    [...endingPatientIds]
      .filter((patientId) => !openingPatientIds.has(patientId))
      .map((patientId) => rosterFromMove(patientId, movesByPatient.get(patientId)?.latestEntry)),
  );

  const removedRoster = sortByPatientName(
    [...openingPatientIds]
      .filter((patientId) => !endingPatientIds.has(patientId))
      .map((patientId) => rosterFromMove(patientId, movesByPatient.get(patientId)?.latestExit)),
  );

  return {
    kpis: {
      opening,
      added,
      removed,
      ending,
      net,
      pctChange: pct.pctChange,
      pctChangeLabel: pct.pctChangeLabel,
    },
    byClient,
    activityRows,
    openingRoster,
    addedRoster,
    removedRoster,
  };
}

export { CHANGE_TYPES };
